import { useState, useEffect } from 'react'
import {
  getInquiry,
  saveInquiry,
  getGeneralInquiry,
  getMessageTemplate,
} from 'lib/services/inquiry'
import { useCartFormData, removeItem } from 'lib/services/cart'
import Image from 'next/image'
import { addToCartEvent, offerRequestSubmittedEvent } from 'lib/services/gtm'
import { fromUnix, toUnix } from 'lib/utils/dates'
import { scrollTo } from 'lib/utils/scrolls'
import { setUserInfo, getUserInfo } from 'lib/services/store'
import Link from 'components/Navigation/WrapperLink'
import SendRequests from 'components/Cart/SendRequests'
import styles from './AddInquiry.module.scss'
import Select from './Select'
import Number from './Number'
import DatePicker from './DatePicker'

const orderSuccess = {
  heading: 'Hurraa! Tarjouspyyntö lähetetty',
  body: 'Tarjouspyyntösi on lähetetty ja palveluntarjoaja pyrkii vastaamaan sinulle 24h sisällä.\n\nEtsi nyt lisää vaihtoehtoja tapahtumaasi tai inspiroidu!',
}

const cartSuccess = {
  heading: 'Tarjouspyyntö lisätty tarjouskoriin!',
  body: 'Jatka selailua löytääksesi muita loistavia vaihtoehtoja tarjouskoriin!',
}

const CartButton = ({ callback }) => (
  <button
    className={`btn btn--secondary mt-4 ${styles.cartBtn}`}
    onClick={callback}
  >
    <Image src="/img/cart-green.svg" width="18" height="18" objectFit="fill" />
  </button>
)

export default function AddInquiry({ event }) {
  const { id, title, locations, categories } = event

  const initialLocation = locations.length > 0 ? locations[0].name : null
  const initialCategory = categories.length > 0 ? categories[0].name : null
  const formData = useCartFormData(false)

  const [location, setLocation] = useState(initialLocation)
  const [eventType, setEventType] = useState(initialCategory)
  const [dateTime, setDateTime] = useState(new Date())
  const [guests, setGuests] = useState(0)
  const [message, setMessage] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isDirect, setIsDirect] = useState(false)
  const [additionalDetailsVisible, setAdditionalVisible] = useState(false)
  const [items, setItems] = useState([])
  const [successMessages, setSuccessMessages] = useState(cartSuccess)
  const [isFlexible, setIsFlexible] = useState(false)

  /**
   * Get data from localstorage.
   * Localstorage only exists on client side,
   * so run it in useEffect.
   */
  useEffect(() => {
    // Use global selection for defaults, if available.
    const generalInquiry = getGeneralInquiry()
    const data = getInquiry(id)

    if (getUserInfo('name')) formData.name.set(getUserInfo('name'))
    if (getUserInfo('email')) formData.email.set(getUserInfo('email'))
    if (getUserInfo('phone')) formData.phone.set(getUserInfo('phone'))
    if (data.location) setLocation(data.location)
    if (data.eventType) setEventType(data.eventType)
    if (data.dateTime) setDateTime(fromUnix(data.dateTime))
    if (data.guests) setGuests(data.guests)
    if (data.message) setMessage(data.message)

    if (!data.dateTime) {
      if (generalInquiry.dateTime.date !== '') {
        setDateTime(fromUnix(generalInquiry.dateTime.date))
      }
    }

    if (!data.location) {
      if (generalInquiry.location) {
        const isValidLocation = locations.some(
          (loc) => loc.name === generalInquiry.location,
        )

        if (isValidLocation) {
          setLocation(generalInquiry.location)
        }
      }
    }

    if (!data.eventType) {
      if (generalInquiry.eventType) {
        const isValidEventType = categories.some(
          (loc) => loc.name === generalInquiry.eventType,
        )

        if (isValidEventType) {
          setEventType(generalInquiry.eventType)
        }
      }
    }

    setIsSubmitted(false)
  }, [event])

  const addInquiryToList = () => {
    const res = saveInquiry(
      id,
      location,
      eventType,
      toUnix(dateTime),
      guests.toString(),
      message,
      isFlexible,
    )
    if (res) {
      setSuccessMessages(cartSuccess)
      addToCartEvent({ name: title })
      setIsSubmitted(true)
      scrollTo('#tarjous')
    }
  }

  /**
   * Triggers cleanup on successful order send.
   * Empties cart & shows thankyou.
   */
  const triggerSuccess = () => {
    setSuccessMessages(orderSuccess)
    removeItem(id)
    offerRequestSubmittedEvent(items)
    setItems([
      {
        id,
        location,
        eventType,
        dateTime: toUnix(dateTime),
        guests: 0,
        message: '',
      },
    ])
    setLocation(initialLocation)
    setEventType(initialCategory)
    setDateTime(new Date())
    setGuests(0)
    setMessage('')
    setIsSubmitted(true)
    setIsDirect(true)
  }

  // Handles event stage
  const handleChange = ({ target }) => {
    const { value, name } = target
    if (formData[name]) {
      formData[name].set(value)
      setUserInfo(name, value)
    }
  }

  const sendInquiry = () => {
    saveInquiry(
      id,
      location,
      eventType,
      toUnix(dateTime),
      guests.toString(),
      message,
      isFlexible,
    )
    setAdditionalVisible(!additionalDetailsVisible)
  }

  useEffect(() => {
    setItems([
      { id, location, eventType, dateTime: toUnix(dateTime), guests, message },
    ])
  }, [location, eventType, dateTime, guests, message])

  /**
   * When message field is clicked,
   * we may want to prefill it with structure for message.
   * Only do it if message is currently empty.
   */
  const maybeAddDefaultMessage = (value) => {
    if (!value) {
      setMessage(getMessageTemplate())
    }
  }

  return (
    <section className={styles.section} id="tarjous">
      <div className={styles.wrap}>
        {!isSubmitted ? (
          <>
            <h2 className={styles.title}>Tarjouspyyntösi</h2>
            <dl className={styles.details}>
              {location && (
                <>
                  <dt className={styles.detailHeading}>Sijainti:</dt>
                  <dd className={styles.detailContent}>
                    <Select
                      options={locations}
                      active={location}
                      callback={(ev) => {
                        setLocation(ev)
                        handleChange({
                          target: { name: 'location', value: ev },
                        })
                      }}
                    />
                  </dd>
                </>
              )}
              {eventType && (
                <>
                  <dt className={styles.detailHeading}>Tapahtuma:</dt>
                  <dd className={styles.detailContent}>
                    <Select
                      options={categories}
                      active={eventType}
                      callback={(ev) => {
                        setEventType(ev)
                        handleChange({
                          target: { name: 'eventType', value: ev },
                        })
                      }}
                    />
                  </dd>
                </>
              )}
              <dt className={styles.detailHeading}>Päivämäärä:</dt>
              <dd className={styles.detailContent}>
                <DatePicker
                  date={dateTime}
                  callback={(ev) => {
                    setDateTime(ev)
                    handleChange({
                      target: { name: 'dateTime', value: ev },
                    })
                  }}
                />
              </dd>
              <dt className={styles.detailHeading}>Joustava Aika:</dt>
              <dd className={styles.detailContent}>
                <input
                  id="isFlexible"
                  name="isFlexible"
                  type="checkbox"
                  className={styles.checkbox}
                  checked={isFlexible}
                  onChange={(ev) => {
                    setIsFlexible(ev.target.checked)
                    handleChange({
                      target: { name: 'isFlexible', value: ev.target.checked },
                    })
                  }}
                />
              </dd>
              <dt className={styles.detailHeading}>Henkilömäärä:</dt>
              <dd className={styles.detailContent}>
                <Number
                  active={guests}
                  callback={(ev) => {
                    setGuests(ev)
                    handleChange({
                      target: { name: 'guests', value: parseInt(ev, 10) },
                    })
                  }}
                />
              </dd>
            </dl>
            <div className={styles.messageWrap}>
              <label className="no-style small">
                Viesti
                <textarea
                  className={styles.message}
                  onChange={(e) => {
                    setMessage(e.target.value)
                    handleChange({
                      target: { name: 'message', value: e.target.value },
                    })
                  }}
                  value={message}
                  placeholder={getMessageTemplate()}
                  onClick={(e) => maybeAddDefaultMessage(e.target.value)}
                  rows="4"
                />
              </label>
            </div>

            {additionalDetailsVisible ? (
              <div className="">
                <form onSubmit={(e) => e.preventDefault}>
                  <div className={styles.inputRow}>
                    <label htmlFor={`${id}-name`}></label>
                    <input
                      id={`${id}-name`}
                      name="name"
                      type="text"
                      placeholder="Nimi/Yritys:"
                      value={formData.name.value}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.inputRow}>
                    <label htmlFor={`${id}-email`}></label>
                    <input
                      id={`${id}-email`}
                      name="email"
                      type="email"
                      value={formData.email.value}
                      placeholder="Sähköpostiosoite:"
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.inputRow}>
                    <label htmlFor={`${id}-phone`}></label>
                    <input
                      id={`${id}-phone`}
                      name="phone"
                      type="text"
                      value={formData.phone.value}
                      placeholder="Puhelinnumero:"
                      onChange={handleChange}
                    />
                  </div>
                </form>
                <SendRequests
                  formData={formData}
                  sessionRequired={false}
                  items={items}
                  triggerSuccess={triggerSuccess}
                  singleOffer={id}
                >
                  <CartButton callback={addInquiryToList} />
                </SendRequests>
              </div>
            ) : (
              <div className={styles.btnRow}>
                <button
                  className={`btn btn--primary ${styles.sendBtn}`}
                  onClick={sendInquiry}
                >
                  Lähetä tarjouspyyntö
                </button>
                <button
                  className={`btn btn--pink ${styles.sendBtn}`}
                  onClick={addInquiryToList}
                >
                  Lisää tarjouskoriin
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <h6 className={styles.titleSuccess}>{successMessages.heading}</h6>
            <div className={`${styles.imageSuccess}`}>
              <Image
                src="/img/happy-cart.png"
                width="400"
                height="180"
                objectFit="contain"
              />
            </div>

            <p className="text-center text-break-spaces">
              {successMessages.body}
            </p>

            <Link data={{ slug: '/tapahtumat', type: 'event_archive' }}>
              <button className={`btn btn--primary ${styles.addBtn}`}>
                Jatka selailua
              </button>
            </Link>
            {isDirect && (
              <Link
                data={{
                  slug: '/inspiraatiota-tapahtumaasi',
                  type: '',
                }}
              >
                <button
                  className={`btn btn--secondary btn--pink ${styles.addBtn}`}
                >
                  Inspiroidu
                </button>
              </Link>
            )}
            {!isDirect && (
              <Link data={{ slug: '/tarjouskori', type: '' }}>
                <button
                  className={`btn btn--secondary btn--pink ${styles.addBtn}`}
                >
                  Tarjouskoriin
                </button>
              </Link>
            )}
          </>
        )}
      </div>
    </section>
  )
}
